import { Form, Head } from "@inertiajs/react"

import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import AppLayout from "@/layouts/app-layout"
import { adminPropertiesPath } from "@/routes"
import type { BreadcrumbItem, DemographicRing, Property } from "@/types"

interface Props {
  property: Property
}

const ringSlots = [0, 1, 2]

const ringMetrics: Array<{ key: keyof DemographicRing; label: string; placeholder?: string }> = [
  { key: "household_income", label: "HH Income ($)", placeholder: "60836" },
  { key: "daytime_population", label: "Daytime Pop.", placeholder: "11669" },
  { key: "population", label: "Population", placeholder: "5000" },
  { key: "median_age", label: "Median Age", placeholder: "36" },
  { key: "employees", label: "Employees", placeholder: "14900" },
]

function ringValue(rings: DemographicRing[] | undefined, index: number, key: keyof DemographicRing) {
  const v = rings?.[index]?.[key]
  return v == null ? "" : String(v)
}

export default function Edit({ property }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Properties", href: adminPropertiesPath() },
    { title: property.name, href: `/admin/properties/${property.id}` },
    { title: "Edit", href: `/admin/properties/${property.id}/edit` },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Edit ${property.name}`} />

      <div className="max-w-3xl space-y-6 p-6">
        <div>
          <h1 className="text-2xl font-semibold">Edit Property</h1>
          <p className="text-muted-foreground text-sm">
            Edit fields used across both brochure styles (Marketing & Listing).
          </p>
        </div>

        <Form
          method="patch"
          action={`/admin/properties/${property.id}`}
          encType="multipart/form-data"
          className="space-y-6"
        >
          {({ errors, processing }) => (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Basics</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-2">
                    <Label htmlFor="name">Property Name</Label>
                    <Input id="name" name="name" defaultValue={property.name} />
                    <InputError messages={errors.name} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="address">Address</Label>
                    <Input id="address" name="address" defaultValue={property.address} />
                    <InputError messages={errors.address} />
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-1 grid gap-2">
                      <Label htmlFor="city">City</Label>
                      <Input id="city" name="city" defaultValue={property.city} />
                      <InputError messages={errors.city} />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="state">State</Label>
                      <Input id="state" name="state" defaultValue={property.state} maxLength={2} />
                      <InputError messages={errors.state} />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="zip_code">ZIP Code</Label>
                      <Input id="zip_code" name="zip_code" defaultValue={property.zip_code} />
                      <InputError messages={errors.zip_code} />
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="description">Internal Description</Label>
                    <Textarea
                      id="description"
                      name="description"
                      defaultValue={property.description ?? ""}
                      rows={3}
                    />
                    <InputError messages={errors.description} />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Marketing Copy</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-2">
                    <Label htmlFor="tagline">Tagline (shown above the fold on Marketing brochures)</Label>
                    <Textarea
                      id="tagline"
                      name="tagline"
                      defaultValue={property.tagline ?? ""}
                      rows={2}
                      placeholder="Located directly in front of The Crossroads Mall…"
                    />
                    <InputError messages={errors.tagline} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="marketing_description">Long Description (used on Listing sheet)</Label>
                    <Textarea
                      id="marketing_description"
                      name="marketing_description"
                      defaultValue={property.marketing_description ?? ""}
                      rows={4}
                    />
                    <InputError messages={errors.marketing_description} />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Building Details (Listing brochure)</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="property_type">Property Type</Label>
                      <Input id="property_type" name="property_type" defaultValue={property.property_type ?? ""} placeholder="Retail" />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="subtype">Subtype</Label>
                      <Input id="subtype" name="subtype" defaultValue={property.subtype ?? ""} placeholder="Specialty Center" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="tenancy">Tenancy</Label>
                      <Input id="tenancy" name="tenancy" defaultValue={property.tenancy ?? ""} placeholder="Multiple" />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="total_building_sqft">Total Building SqFt</Label>
                      <Input
                        id="total_building_sqft"
                        name="total_building_sqft"
                        type="number"
                        defaultValue={property.total_building_sqft ?? ""}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="year_built">Year Built</Label>
                      <Input id="year_built" name="year_built" type="number" defaultValue={property.year_built ?? ""} />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="stories">Stories</Label>
                      <Input id="stories" name="stories" type="number" defaultValue={property.stories ?? ""} />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="land_acres">Land Acres</Label>
                      <Input
                        id="land_acres"
                        name="land_acres"
                        type="number"
                        step="0.01"
                        defaultValue={property.land_acres ?? ""}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="class_rating">Class</Label>
                      <Input id="class_rating" name="class_rating" defaultValue={property.class_rating ?? ""} placeholder="A / B / C" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="cross_street">Cross Street</Label>
                      <Input id="cross_street" name="cross_street" defaultValue={property.cross_street ?? ""} />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="zoning">Zoning</Label>
                      <Input id="zoning" name="zoning" defaultValue={property.zoning ?? ""} placeholder="CC Commercial" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Demographics & Area Map</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid gap-4">
                    <div className="grid max-w-xs gap-2">
                      <Label htmlFor="demographics_form_ring_unit">Ring Unit</Label>
                      <Select
                        name="demographics_form[ring_unit]"
                        defaultValue={property.demographic_ring_unit ?? "miles"}
                      >
                        <SelectTrigger id="demographics_form_ring_unit">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="minutes">Drive-time minutes</SelectItem>
                          <SelectItem value="miles">Miles</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-muted-foreground text-xs">
                        Choose whether the rings represent drive-time minutes (Treystar marketing style) or radial miles (Crexi style).
                      </p>
                    </div>

                    {ringSlots.map((idx) => (
                      <div key={idx} className="rounded-md border p-4">
                        <div className="mb-3 flex items-center justify-between">
                          <p className="text-sm font-semibold">Ring {idx + 1}</p>
                          <p className="text-muted-foreground text-xs">Leave all fields blank to omit this ring.</p>
                        </div>
                        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
                          <div className="grid gap-2">
                            <Label htmlFor={`ring_${idx}_distance`} className="whitespace-nowrap">Distance</Label>
                            <Input
                              id={`ring_${idx}_distance`}
                              name={`demographics_form[rings][${idx}][distance]`}
                              type="number"
                              defaultValue={ringValue(property.demographic_rings, idx, "distance")}
                              placeholder={String([5, 10, 15][idx] ?? "")}
                            />
                          </div>
                          {ringMetrics.map((metric) => (
                            <div key={metric.key} className="grid gap-2">
                              <Label htmlFor={`ring_${idx}_${metric.key}`} className="whitespace-nowrap">
                                {metric.label}
                              </Label>
                              <Input
                                id={`ring_${idx}_${metric.key}`}
                                name={`demographics_form[rings][${idx}][${metric.key}]`}
                                type="number"
                                defaultValue={ringValue(property.demographic_rings, idx, metric.key)}
                                placeholder={metric.placeholder}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="hero_image">Property Hero Image</Label>
                    <p className="text-muted-foreground text-xs">
                      Used as the main visual on page 1 of the Marketing brochure (aerial / iconic building shot). Falls back to the suite's first photo if not set.
                    </p>
                    <Input id="hero_image" name="hero_image" type="file" accept="image/*" />
                    {property.hero_image_url && (
                      <img src={property.hero_image_url} alt="Current hero image" className="mt-2 max-h-48 w-auto rounded border" />
                    )}
                    <InputError messages={errors.hero_image} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="area_map">Area / Site Map Image</Label>
                    <p className="text-muted-foreground text-xs">
                      Used on the site plan page of the Marketing brochure (aerial of the center with tenant markers).
                    </p>
                    <Input id="area_map" name="area_map" type="file" accept="image/*" />
                    {property.area_map_url && (
                      <img src={property.area_map_url} alt="Current area map" className="mt-2 max-h-48 w-auto rounded border" />
                    )}
                    <InputError messages={errors.area_map} />
                  </div>
                </CardContent>
              </Card>

              <div className="flex gap-3">
                <Button type="submit" disabled={processing}>Save Changes</Button>
                <Button variant="outline" type="button" onClick={() => history.back()}>
                  Cancel
                </Button>
              </div>
            </>
          )}
        </Form>
      </div>
    </AppLayout>
  )
}
